// panels/AlertsPanel.jsx
import React, { useState } from 'react';
import { useStore } from '../../../store/useStore';
import { alertsApi } from '../../../services/api';
import { CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { PanelWrapper } from './PanelWrapper';

const SEV_STYLES = {
  CRITICAL: { color: '#fca5a5', bg: 'rgba(239,68,68,.18)', border: 'rgba(239,68,68,.35)' },
  WARNING:  { color: '#fbbf24', bg: 'rgba(245,158,11,.15)', border: 'rgba(245,158,11,.3)' },
  INFO:     { color: '#60a5fa', bg: 'rgba(59,130,246,.15)', border: 'rgba(59,130,246,.3)' },
};

const STATUS_ICONS = {
  OPEN:         { icon: AlertTriangle, color: '#ef4444' },
  ACKNOWLEDGED: { icon: Clock,         color: '#f59e0b' },
  RESOLVED:     { icon: CheckCircle,   color: '#22c55e' },
};

function AlertRow({ alert, onAck, onResolve }) {
  const sev  = SEV_STYLES[alert.severity]   || SEV_STYLES.INFO;
  const stat = STATUS_ICONS[alert.status]   || STATUS_ICONS.OPEN;
  const Icon = stat.icon;
  const ago  = alert.triggeredAt
    ? timeDiff(new Date(alert.triggeredAt))
    : '';

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '20px 1fr 80px 90px 110px',
      gap: 8, alignItems: 'center',
      padding: '7px 10px',
      borderBottom: '1px solid rgba(255,255,255,0.04)',
      fontSize: 11,
    }}
      className="hover:bg-white/[0.02]"
    >
      <Icon size={12} style={{ color: stat.color, flexShrink: 0 }} />

      <div style={{ minWidth: 0 }}>
        <div style={{
          color: 'var(--text)', fontWeight: 500,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {alert.title}
        </div>
        <div style={{ color: 'var(--text-3)', fontSize: 10, marginTop: 1 }}>{alert.source}</div>
      </div>

      <span style={{
        fontSize: 9, fontWeight: 700, textAlign: 'center',
        color: sev.color, background: sev.bg,
        border: `1px solid ${sev.border}`,
        borderRadius: 4, padding: '2px 6px',
      }}>
        {alert.severity}
      </span>

      <span style={{ color: 'var(--text-3)', fontSize: 10 }}>{ago}</span>

      <div style={{ display: 'flex', gap: 4 }}>
        {alert.status === 'OPEN' && (
          <button
            onClick={() => onAck(alert._id)}
            style={{
              fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
              border: '1px solid rgba(245,158,11,.4)', background: 'rgba(245,158,11,.1)',
              color: '#fbbf24', cursor: 'pointer',
            }}>
            Ack
          </button>
        )}
        {alert.status !== 'RESOLVED' && (
          <button
            onClick={() => onResolve(alert._id)}
            style={{
              fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
              border: '1px solid rgba(34,197,94,.4)', background: 'rgba(34,197,94,.1)',
              color: '#22c55e', cursor: 'pointer',
            }}>
            Resolve
          </button>
        )}
      </div>
    </div>
  );
}

function timeDiff(date) {
  const diff = Date.now() - date;
  if (diff < 60000)      return `${Math.round(diff / 1000)}s ago`;
  if (diff < 3600000)    return `${Math.round(diff / 60000)}m ago`;
  if (diff < 86400000)   return `${Math.round(diff / 3600000)}h ago`;
  return `${Math.round(diff / 86400000)}d ago`;
}

export function AlertsPanel({ loading }) {
  const { alerts, variables, updateAlert } = useStore();
  const [page, setPage] = useState(0);
  const PAGE = 8;

  const filtered = alerts.filter(a => {
    if (a.status === 'RESOLVED') return false;
    if (variables?.source   && variables.source   !== 'ALL' && a.source   !== variables.source)   return false;
    if (variables?.severity && variables.severity !== 'ALL' && a.severity !== variables.severity) return false;
    return true;
  });

  const paged = filtered.slice(page * PAGE, (page + 1) * PAGE);
  const pages = Math.ceil(filtered.length / PAGE);

  async function handleAck(id) {
    try {
      const updated = await alertsApi.acknowledge(id, 'dashboard-user');
      updateAlert(updated);
    } catch {}
  }

  async function handleResolve(id) {
    try {
      const updated = await alertsApi.resolve(id);
      updateAlert(updated);
    } catch {}
  }

  function exportCsv() {
    const header = 'title,severity,status,source,triggeredAt';
    const rows   = filtered.map(a => [a.title, a.severity, a.status, a.source, a.triggeredAt].join(','));
    const blob   = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const el = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: 'alerts.csv' });
    el.click();
  }

  return (
    <PanelWrapper
      title="Active Alerts"
      subtitle={`${filtered.length} open`}
      datasource="MongoDB"
      loading={loading}
      onExport={exportCsv}
    >
      {/* Table header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '20px 1fr 80px 90px 110px',
        gap: 8, padding: '6px 10px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-tertiary)',
        fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.8px', color: 'var(--text-3)',
      }}>
        <div />
        <div>Title</div>
        <div>Severity</div>
        <div>Age</div>
        <div>Actions</div>
      </div>

      {/* Rows */}
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {paged.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)', fontSize: 12 }}>
            No active alerts
          </div>
        ) : (
          paged.map(a => (
            <AlertRow key={a._id} alert={a} onAck={handleAck} onResolve={handleResolve} />
          ))
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          padding: '6px', borderTop: '1px solid var(--border)',
          fontSize: 11, color: 'var(--text-3)',
        }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '2px 6px' }}>
            ‹
          </button>
          <span>{page + 1} / {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page === pages - 1}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '2px 6px' }}>
            ›
          </button>
        </div>
      )}
    </PanelWrapper>
  );
}
